/**
 * Number-format vocabulary — ONE definition of every value `numberFormat` can
 * hold, so the client and the server can never accept different sets.
 *
 * They already did once: the amount column filters shipped a hand-written server
 * enum ('european' | 'american' | 'european_space' | 'minimal', copied from the
 * legacy `formatters.ts` map) while the client sends
 * `userConfigService.getConfig().numberFormat`, whose real values come from
 * NUMBER_FORMAT_OPTIONS ('europeo-puntos', …). Every amount filter 400'd.
 *
 * Consumers (both derive, neither restates):
 *  - frontend `NumberFormat` (shared/types/services.types.ts)
 *  - backend  `AmountFilterNumberFormat` + the /matching route enums
 *    (platform/shared-utils/localized-amount-filter.utils.ts)
 */

import { NUMBER_FORMAT_OPTIONS } from './constants.js'

/** The four values the profile preferences UI can persist today. */
export type ConfigurableNumberFormat = (typeof NUMBER_FORMAT_OPTIONS)[number]['value']

/**
 * Values persisted (or defaulted) BEFORE the current option set existed. They
 * are still read: `resolveSeparatorsForNumberFormat`
 * (frontend shared/services/user-config.number-format.ts) maps every one of
 * them, and 'european' is still the frontend's in-memory default config value —
 * so a stored preference, or a user who never opened Preferences, can send any
 * of these. Never drop one without a data migration.
 */
export const LEGACY_NUMBER_FORMAT_VALUES = [
  'european',
  'american',
  'european_space',
  'minimal',
  'international',
  'compact',
] as const

export type LegacyNumberFormat = (typeof LEGACY_NUMBER_FORMAT_VALUES)[number]

/** Every value the client can put in `numberFormat`. */
export type NumberFormatValue = ConfigurableNumberFormat | LegacyNumberFormat

const CONFIGURABLE_NUMBER_FORMAT_VALUES: readonly ConfigurableNumberFormat[] =
  NUMBER_FORMAT_OPTIONS.map((option) => option.value)

/**
 * Runtime list of the whole vocabulary — the oracle both packages' contract
 * tests assert against, and the source of the backend's request enum.
 */
export const ALL_NUMBER_FORMAT_VALUES: readonly NumberFormatValue[] = [
  ...CONFIGURABLE_NUMBER_FORMAT_VALUES,
  ...LEGACY_NUMBER_FORMAT_VALUES,
]

/** Runtime guard for values arriving from storage / an untyped API client. */
export function isNumberFormatValue(candidate: string): candidate is NumberFormatValue {
  return (ALL_NUMBER_FORMAT_VALUES as readonly string[]).includes(candidate)
}
